/**
 * api/reports.ts —— 双周报（§五 5.6）
 *
 * ⚠ 提交率、逾期天数、应交清单、距截止天数**全部由服务端算好下发**，前端一个都不许自己求
 *   （红线 4）。看到这里出现「今天 − 结束日」这种减法就是口径分叉的开始。
 * 🔴 三条只在这个模块出现的服务端约束，前端要按它们设计交互（**不是靠前端拦，是服务端会拒**）：
 *   ① **提交单向不可逆**：没有撤回 / 重新提交 / 确认 / 退回四个接口，不是漏写；
 *   ② **历史期补交不同步 KR**：`ReportDetailView.canSyncKr` 为 false 时**不要带 krSync**，
 *      带了会被 403 拒掉（前端不渲染 KR 区不算权限）；
 *   ③ **版本冲突整单拒绝**：`VERSION_CONFLICT` 带 `conflicts` 逐条前后值，
 *      与 OKR 页共用同一个冲突弹窗（`pages/okr.ts` 的 `krConflictModal`），不要写第二个。
 */
import { get, post, put, type ReqOptions } from './client.js';
import type {
  KrProgressSyncItem,
  ReportDetailView,
  ReportListView,
  ReportOwedView,
  ReportSaveInput,
  SubmitRate,
} from '@shared-types';

/**
 * 列表整页。
 * ⚠ `periodNo` 传 null / 不合法时服务端会**落到默认期**（进行中的那期，没有就落最近一期已结束的），
 *   不报错——双周报是入口页，报错会让人无处可去。
 */
export const list = (periodNo: number | null, opts?: ReqOptions) =>
  get<ReportListView>(`/api/reports${periodNo == null ? '' : `?periodNo=${periodNo}`}`, opts);

/**
 * 详情 / 编辑整页（同一个接口，§五 5.6 补充 G2）。
 * ⚠ 编辑页要的三样都在里面：正文初值、KR 快照（含 `version`）、能不能同步。
 *   **不要为编辑页再打一次 OKR 接口**——那样正文与 KR 版本会取自两个时点。
 */
export const detail = (teamId: string, periodNo: number, opts?: ReqOptions) =>
  get<ReportDetailView>(
    `/api/reports/${encodeURIComponent(teamId)}/${encodeURIComponent(String(periodNo))}`,
    opts,
  );

export interface ReportWriteInput extends Omit<ReportSaveInput, 'krSync'> {
  /** ⚠ 只有当前期能带；历史期带了会被服务端 403 拒（RULES §二 2） */
  krSync?: KrProgressSyncItem[];
}

/**
 * 首次提交 / 补交。
 * 🔴 地址是 `/api/reports/submit` 而不是 §五 5.6 原写的 `/api/reports/:id/submit`（补充 G3）：
 *   二期不做草稿，未提交 = 库里根本没有这条记录，**首次提交时没有 id 可填进 URL**。
 * ⚠ 出参是**提交后的详情整页**：提交完直接落到详情页，不用再打一次接口。
 */
export const submit = (input: ReportWriteInput) => post<ReportDetailView>('/api/reports/submit', input);

/**
 * 修订已提交内容。
 * ⚠ 状态保持 `submitted` —— 保存不是"重新提交"，也不会把提交人 / 提交时间 / 逾期快照改掉。
 */
export const save = (id: string, input: ReportWriteInput) =>
  put<ReportDetailView>(`/api/reports/${encodeURIComponent(id)}`, input);

/** 某期提交率（给别的页面用的轻量口子）。⚠ 分母为 0 时 pct 为 0，不是 NaN */
export const rate = (periodNo: number, opts?: ReqOptions) =>
  get<SubmitRate>(`/api/reports/rate/${encodeURIComponent(String(periodNo))}`, opts);

/** 上一期欠交名单。⚠ 返回的是【上一期】——本期未提交不是逾期（红线 3） */
export const owed = (opts?: ReqOptions) => get<ReportOwedView>('/api/reports/owed', opts);
